import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import Button from '../ui/ButtonComponent';
import WizardStep1 from '../wizard/WizardStep1';
import WizardStep2 from '../wizard/WizardStep2';
import WizardStep3 from '../wizard/WizardStep3';
import WizardStep4 from '../wizard/WizardStep4';
import WizardStep5 from '../wizard/WizardStep5';
import WizardStepFinal from '../wizard/WizardStepFinal';

interface PublishingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  postData?: {
    title?: string;
    description?: string;
    platforms?: string[];
    mediaType?: 'image' | 'video';
    mediaFiles?: File[];
  };
}

export interface WizardData {
  // Step 1 - Post Details
  title: string;
  description: string;
  platforms: string[];
  mediaType: 'image' | 'video';
  thumbnail: File | null;
  mediaFiles: File[];

  // Step 2 - Optimization
  hashtags: string[];
  keywords: string[];
  scheduledTime: string;

  // Step 3 - Monetization
  monetizationEnabled: boolean;
  adFormats: {
    display: boolean;
    overlay: boolean;
    skippable: boolean;
    nonSkippable: boolean;
    midRoll: boolean;
  };

  // Step 4 - Compliance
  ageRestriction: string;
  adSuitability: {
    profanity: string;
    violence: string;
    adultContent: string;
    shockingContent: string;
    harmfulActs: string;
    drugsAlcohol: string;
    firearms: string;
    sensitivePolitics: string;
    controversialIssues: string;
  };
  subtitlesEnabled: boolean;
  altText: string;
  complianceChecksComplete: boolean;

  // Step 5 - Video Elements
  endScreenEnabled: boolean;
  cards: Array<{ type: string; title: string }>;
  subtitlesFile: File | null;

  // Final - Schedule & Visibility
  visibility: string;
  publishDate: string;
  publishTime: string;
  timezone: string;
}

const STEPS = [
  { id: 1, name: 'Post Details', description: 'Title, description & media' },
  { id: 2, name: 'Optimization', description: 'SEO & hashtags' },
  { id: 3, name: 'Monetization', description: 'Revenue settings' },
  { id: 4, name: 'Compliance & Safety', description: 'Ad suitability & checks' },
  { id: 5, name: 'Video Elements', description: 'End screens & cards' },
  { id: 6, name: 'Schedule & Visibility', description: 'Publish settings' },
];

export default function PublishingWizard({ isOpen, onClose, postData }: PublishingWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState<WizardData>({
    title: postData?.title || '',
    description: postData?.description || '',
    platforms: postData?.platforms || [],
    mediaType: postData?.mediaType || 'image',
    thumbnail: null,
    mediaFiles: postData?.mediaFiles || [],
    hashtags: [],
    keywords: [],
    scheduledTime: '',
    monetizationEnabled: false,
    adFormats: {
      display: false,
      overlay: false,
      skippable: false,
      nonSkippable: false,
      midRoll: false,
    },
    ageRestriction: 'all',
    adSuitability: {
      profanity: 'none',
      violence: 'none',
      adultContent: 'none',
      shockingContent: 'none',
      harmfulActs: 'none',
      drugsAlcohol: 'none',
      firearms: 'none',
      sensitivePolitics: 'none',
      controversialIssues: 'none',
    },
    subtitlesEnabled: false,
    altText: '',
    complianceChecksComplete: false,
    endScreenEnabled: false,
    cards: [],
    subtitlesFile: null,
    visibility: 'public',
    publishDate: '',
    publishTime: '',
    timezone: 'America/New_York',
  });

  if (!isOpen) return null;

  const updateWizardData = (data: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    // Skip Step 3 (Monetization) if not video or platform doesn't support it
    if (currentStep === 2 && (!wizardData.platforms.includes('YouTube') || wizardData.mediaType !== 'video')) {
      setCurrentStep(4);
      return;
    }

    // Skip Step 5 (Video Elements) if not video
    if (currentStep === 4 && wizardData.mediaType !== 'video') {
      setCurrentStep(6);
      return;
    }

    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    // Skip Step 5 (Video Elements) if not video
    if (currentStep === 6 && wizardData.mediaType !== 'video') {
      setCurrentStep(4);
      return;
    }

    // Skip Step 3 (Monetization) if not video or platform doesn't support it
    if (currentStep === 4 && (!wizardData.platforms.includes('YouTube') || wizardData.mediaType !== 'video')) {
      setCurrentStep(2);
      return;
    }

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePublish = () => {
    const scheduleDetails = `${wizardData.publishDate} at ${wizardData.publishTime}`;
    const platformList = wizardData.platforms.join(', ');
    const complianceStatus = wizardData.complianceChecksComplete ? '✓ All checks passed' : '⚠ Some checks pending';
    const monetizationStatus = wizardData.monetizationEnabled ? 'Monetization: ON' : 'Monetization: OFF';
    const videoElementsStatus = wizardData.endScreenEnabled || wizardData.cards.length > 0 ? '✓ Video elements added' : '';

    alert(
      `Post Scheduled Successfully!\n\n` +
      `Title: ${wizardData.title}\n` +
      `Platforms: ${platformList}\n` +
      `${complianceStatus}\n` +
      `${monetizationStatus}\n` +
      `${videoElementsStatus}\n` +
      `Scheduled for: ${scheduleDetails}\n` +
      `Visibility: ${wizardData.visibility.toUpperCase()}\n\n` +
      `Your post will appear in the Content Scheduler with compliance flags.`
    );
    onClose();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return wizardData.title.trim() !== '' && wizardData.platforms.length > 0;
      case 2:
        return true;
      case 3:
        return true;
      case 4:
        return wizardData.complianceChecksComplete;
      case 5:
        return true;
      case 6:
        return wizardData.publishDate !== '' && wizardData.publishTime !== '';
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WizardStep1 data={wizardData} updateData={updateWizardData} />;
      case 2:
        return <WizardStep2 data={wizardData} updateData={updateWizardData} />;
      case 3:
        return <WizardStep3 data={wizardData} updateData={updateWizardData} />;
      case 4:
        return <WizardStep4 data={wizardData} updateData={updateWizardData} />;
      case 5:
        return <WizardStep5 data={wizardData} updateData={updateWizardData} />;
      case 6:
        return <WizardStepFinal data={wizardData} updateData={updateWizardData} />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[960px] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
          <div>
            <h2 className="text-xl font-semibold text-[#111827]">Publishing Wizard</h2>
            <p className="text-sm text-[#6B7280] mt-1">
              Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          >
            <X size={20} className="text-[#9CA3AF] hover:text-[#6B7280]" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-6 pb-4 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center w-full">
                  <div className="flex items-center w-full">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                        currentStep > step.id
                          ? 'bg-[#10B981] text-white'
                          : currentStep === step.id
                          ? 'bg-[#2563EB] text-white'
                          : 'bg-[#F3F4F6] text-[#9CA3AF]'
                      }`}
                    >
                      {currentStep > step.id ? (
                        <CheckCircle size={16} />
                      ) : (
                        step.id
                      )}
                    </div>
                    {index < STEPS.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 transition-colors ${
                          currentStep > step.id ? 'bg-[#10B981]' : 'bg-[#E5E7EB]'
                        }`}
                      />
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 text-center ${
                      currentStep === step.id
                        ? 'text-[#111827] font-medium'
                        : 'text-[#9CA3AF]'
                    }`}
                  >
                    {step.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[#E5E7EB]">
          <Button
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <Button
                variant="secondary"
                size="md"
                onClick={handleBack}
              >
                Back
              </Button>
            )}
            {currentStep < 6 ? (
              <Button
                variant="primary"
                size="md"
                onClick={handleNext}
                disabled={!canProceed()}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                onClick={handlePublish}
                disabled={!canProceed()}
              >
                Publish
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
